import { Link } from 'react-router'
import {
	type PlanPriceDisplay,
} from '#app/utils/plan-format.ts'

export interface PricingPlan {
	key: 'PRO' | 'BUSINESS' | 'ENTERPRISE'
	name: string
	description: string
	price: PlanPriceDisplay
	features: string[]
	cta: string
	href: string
	featured: boolean
}

export function Pricing({ plans }: { plans: PricingPlan[] }) {
	return (
		<section
			id="tarifs"
			style={{ padding: '140px 0', background: 'var(--surface)' }}
		>
			<div className="mx-auto max-w-310 px-5 md:px-10">
				<div className="mx-auto mb-18 max-w-160 text-center">
					<div className="eyebrow mb-6 justify-center">Tarifs</div>
					<h2 className="pb-5">
						Un prix <span className="serif-italic">honnête</span>, pensé
						pour le Québec.
					</h2>
					<p
						style={{
							fontSize: 17,
							color: 'var(--text-secondary)',
							lineHeight: 1.6,
						}}
					>
						Facturation mensuelle ou annuelle (−10%). Tous les plans
						incluent l'hébergement au Canada.
					</p>
				</div>

				<div
					className="grid gap-5"
					style={{
						gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
					}}
				>
					{plans.map((p) => (
						<PlanCard key={p.key} plan={p} />
					))}
				</div>

				<div
					className="mt-12 text-center text-[13px]"
					style={{ color: 'var(--text-secondary)' }}
				>
					Tarifs en CAD, taxes en sus · Facturation mensuelle ou annuelle
				</div>
			</div>
		</section>
	)
}

function PlanCard({ plan }: { plan: PricingPlan }) {
	const { name, description, price, features, cta, href, featured } = plan
	return (
		<div
			className="relative rounded-[18px] p-8"
			style={{
				background: featured ? 'var(--ink)' : 'white',
				color: featured ? 'white' : 'var(--ink)',
				border: featured
					? '1px solid var(--ink)'
					: '1px solid var(--border-soft)',
				boxShadow: featured ? 'var(--shadow-3)' : 'var(--shadow-1)',
			}}
		>
			{featured ? (
				<div
					className="absolute -top-3 left-6 rounded-full px-3 py-1 text-[11px] font-medium uppercase tracking-[0.06em] text-white"
					style={{ background: 'var(--brand)' }}
				>
					Recommandé
				</div>
			) : null}

			<h3
				className="mb-1.5"
				style={{
					fontSize: 22,
					fontWeight: 450,
					letterSpacing: '-0.02em',
					lineHeight: 1.2,
				}}
			>
				{name}
			</h3>
			<div
				className="mb-7 text-[14px]"
				style={{
					color: featured
						? 'rgba(255,255,255,0.65)'
						: 'var(--text-secondary)',
					minHeight: 42,
				}}
			>
				{description}
			</div>

			<div className="mb-7 flex items-baseline gap-1.5">
				{price.isCustom ? (
					<span
						className="font-display"
						style={{ fontSize: 36, fontWeight: 450 }}
					>
						Sur mesure
					</span>
				) : (
					<>
						<span className="text-[18px]" style={{ opacity: 0.6 }}>
							$
						</span>
						<span
							className="font-display"
							style={{
								fontSize: 52,
								fontWeight: 450,
								letterSpacing: '-0.03em',
								lineHeight: 1,
							}}
						>
							{price.monthly}
						</span>
						<span
							className="ml-1 text-[14px]"
							style={{ opacity: 0.6 }}
						>
							/ mois
						</span>
					</>
				)}
			</div>

			<Link
				to={href}
				className="mb-7 flex w-full items-center justify-center rounded-xl text-[14px] font-medium transition-all hover:-translate-y-px"
				style={{
					height: 46,
					background: featured ? 'var(--brand)' : 'transparent',
					color: featured ? 'white' : 'var(--ink)',
					border: featured
						? '1px solid transparent'
						: '1px solid var(--border-soft)',
					boxShadow: featured
						? '0 1px 0 rgba(255,255,255,0.15) inset, 0 8px 24px -8px var(--brand-glow)'
						: 'none',
				}}
			>
				{cta}
			</Link>

			<hr
				className="mb-5"
				style={{
					border: 'none',
					borderTop: `1px solid ${featured ? 'rgba(255,255,255,0.1)' : 'var(--border-soft)'
						}`,
				}}
			/>

			<ul className="flex flex-col gap-3">
				{features.map((f) => (
					<li
						key={f}
						className="flex items-start gap-2.5 text-[14px] leading-tight"
					>
						<svg
							width="14"
							height="14"
							viewBox="0 0 14 14"
							fill="none"
							style={{ flex: 'none', marginTop: 3 }}
						>
							<path
								d="M2.5 7L5.5 10L11.5 4"
								stroke={featured ? '#a89dff' : '#5D2FF0'}
								strokeWidth="1.6"
								strokeLinecap="round"
								strokeLinejoin="round"
							/>
						</svg>
						{f}
					</li>
				))}
			</ul>
		</div>
	)
}
